// components/LoadFailure.jsx
// Created: 2026-07-19
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.0
//
// Shown when a fetch fails, in place of the surface's empty state.
//
// Why this exists: ~19 loaders across the app were written as
// `.catch(() => setX([]))`, so a server error rendered as "there is nothing
// here". The user was told there was no data when the request had actually
// failed. In the genogram that lie reached the database — a failed GET emptied
// the stored relationships, which unlocked the inference pass, which wrote
// guesses over real data.
//
// An empty result and a failed request must not look the same.

import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import Icon from './Icon';
import Button from './ui/Button';

export default function LoadFailure({ theme, what = 'this', onRetry, compact = false }) {
  const pad = compact ? '10px 12px' : '18px 16px';
  const gap = compact ? '8px' : '10px';

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: compact ? 'center' : 'flex-start',
        gap,
        padding: pad,
        margin: compact ? '6px 0' : '10px 0',
        borderRadius: '8px',
        border: '1px solid #ef444455',
        background: '#ef444414',
        color: theme?.text || '#e8e8e8',
      }}
    >
      <Icon icon={AlertTriangle} size={compact ? 13 : 16} style={{ color: '#ef4444', marginTop: compact ? 0 : '1px' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: compact ? '0.72rem' : '0.8rem', fontWeight: 700, lineHeight: 1.35 }}>
          Could not load {what}
        </div>
        <div style={{ fontSize: compact ? '0.66rem' : '0.72rem', color: theme?.textSub || '#888', marginTop: '2px', lineHeight: 1.4 }}>
          The server did not respond. This is not an empty {what} — nothing has been changed.
        </div>
      </div>

      {onRetry && (
        <Button
          theme={theme}
          onClick={onRetry}
          variant="outline"
          size="sm"
          icon={RotateCcw}
          style={{ flexShrink: 0 }}
        >
          Retry
        </Button>
      )}
    </div>
  );
}
